/**
 * Signing Manager for iframe
 *
 * Handles transaction signing with user confirmation inside secure iframe context
 */

import init, * as tss from 'dkls23-wasm'

import { UserRejectedError } from '../../lib/errors'
import { buildConfirmTxTemplateData, buildSignTxTemplateData, TemplateEngine, TemplateLoader } from '../templates'
import { OptedOutAppsManager } from './opted-out-apps-manager'
import { StorageManager } from './storage-manager'
import { TokenRefreshApiClient } from './token-refresh-client'
import { TrustedAppsManager } from './trusted-apps-manager'
import { effectiveTransaction, isMinimalSafeOperation } from './userop-display'
import type { SigningPayload } from '../../lib/wire-userop'
import type {
  EIP712TypedData,
  KeyshareData,
  ProjectInfo,
  RiskAssessment,
  SignTypedDataRequest,
  Transaction
} from './types'
import { fetchProjectCard, type ProjectCard } from './project-card'



type SignStartPayload = SigningPayload | { kind: 'typedData'; typedData: EIP712TypedData }

function signStartBody(
  sessionId: string,
  digest32: `0x${string}`,
  chainPath: string,
  payload?: SignStartPayload
): Record<string, unknown> {
  if (!payload) {
    return { sessionId, digest32, chainPath }
  }
  if (payload.kind === 'userOp') {
    return {
      sessionId,
      digest32,
      chainPath,
      kind: 'userOp',
      chainId: payload.chainId,
      entryPoint: payload.entryPoint,
      userOp: payload.userOp
    }
  }
  return { sessionId, digest32, chainPath, kind: 'typedData', typedData: payload.typedData }
}

export class SigningManager extends TokenRefreshApiClient {
  private wasmLoaded: boolean = false
  private storage: StorageManager
  private trustedApps: TrustedAppsManager
  private optedOutApps: OptedOutAppsManager
  private explorerUrl: string = 'https://beam-explorer.lumia.org'

  constructor() {
    super()
    this.storage = new StorageManager()
    this.trustedApps = new TrustedAppsManager()
    this.optedOutApps = new OptedOutAppsManager()
  }

  /**
   * Initialize WASM module
   */
  async initialize(): Promise<void> {
    if (this.wasmLoaded) return

    try {
      await init()
      this.wasmLoaded = true
      ;(globalThis as any).__tss_wasm_api__ = tss
    } catch (error) {
      console.error('[iframe][Sign] Failed to load WASM:', error)
      throw new Error('Failed to initialize signing WASM module')
    }
  }

  private fetchProjectMetadata(projectId: string): Promise<ProjectCard | null> {
    return fetchProjectCard(projectId)
  }

  /**
   * Sign transaction with user confirmation
   */
  async signTransaction(
    userId: string,
    projectId: string,
    origin: string,
    transaction: Transaction,
    accessToken?: string,
    userProfile?: { avatar?: string | null; displayName?: string | null },
    /** owner address of the project's wallet as TSS reports it; undefined signs with the current keyshare */
    walletOwner?: string
  ): Promise<`0x${string}`> {
    if (!this.wasmLoaded) {
      await this.initialize()
    }

    // Load keyshare from IFRAME localStorage
    const keyshareData = this.storage.loadKeyshareOf(userId, walletOwner)
    if (!keyshareData) {
      throw new Error('No keyshare found. Please complete DKG first.')
    }

    // Get project info (TODO: from backend API)
    const projectInfo: ProjectInfo = {
      id: projectId,
      name: 'Application',
      logoUrl: '',
      website: origin,
      domains: [origin]
    }

    const effective = effectiveTransaction(transaction)

    // Check if this is a minimal safe operation (account deployment)
    const isMinimalOperation = isMinimalSafeOperation(effective)
    if (isMinimalOperation) {
      console.log('[iframe][Sign] Detected minimal safe operation:', {
        to: effective.to,
        value: effective.value,
        data: effective.data,
        reason: 'Account deployment - no funds at risk'
      })
    }

    // Check if app is trusted (auto-approve without confirmation)
    const isTrusted = this.trustedApps.isTrusted(userId, projectId, origin)

    // Skip consent for minimal operations (safe account deployment)
    if (!isTrusted && !isMinimalOperation) {
      // Risk assessment
      const risk = await this.assessRisk(effective)

      // Show user confirmation dialog
      const confirmResult = await this.showConfirmationDialog(
        userId,
        projectId,
        projectInfo,
        origin,
        effective,
        risk,
        userProfile
      )

      if (!confirmResult.confirmed) {
        throw new UserRejectedError('User rejected transaction')
      }

      // If user checked "trust this app", save it
      if (confirmResult.trustApp) {
        this.trustedApps.addTrustedApp(userId, projectId, origin, confirmResult.appName, confirmResult.appLogo)
        // Clear any opt-out flag since user explicitly chose to trust this app
        this.optedOutApps.removeOptedOut(userId, projectId, origin)
      }
    } else if (isMinimalOperation) {
      console.log('[iframe][Sign] Auto-approving minimal safe operation (account deployment)')
    }

    // Use pre-computed digest if provided, otherwise compute hash
    // IMPORTANT: For UserOperations, digest32 is already computed by client
    const digest32 = transaction.digest32 || this.computeTransactionHash(transaction)

    // Perform MPC signing
    const signature = await this.performMPCSigning(userId, keyshareData, digest32, projectId, accessToken, effective.signing)

    return signature
  }

  /**
   * Perform MPC signing protocol
   */
  private async performMPCSigning(
    userId: string,
    keyshareData: KeyshareData,
    digest32: `0x${string}`,
    projectId: string,
    accessToken?: string,
    payload?: SignStartPayload
  ): Promise<`0x${string}`> {
    const keyshare = keyshareData.keyshare_b64
    const sessionId = keyshareData.sessionId

    // Convert digest to base64
    const digestHex = digest32.startsWith('0x') ? digest32.slice(2) : digest32
    const digestBytes = this.hexToBytes(digestHex)
    const digestB64 = btoa(String.fromCharCode(...digestBytes))

    const CHAIN_PATH = 'm'

    // Step 1: Client starts signing locally
    const clientSignStartResult = (tss as any).sign_start(keyshare, digestB64, CHAIN_PATH)

    if (!Array.isArray(clientSignStartResult) || clientSignStartResult.length < 2) {
      throw new Error('Invalid sign_start result format')
    }

    let clientState = clientSignStartResult[0]
    const clientMsg1 = clientSignStartResult[1]

    // Step 2: Server starts signing
    const serverStartResponse = await this.apiCall(
      'POST',
      `/api/tss/${userId}/sign/start`,
      signStartBody(sessionId, digest32, CHAIN_PATH, payload),
      projectId,
      accessToken
    )

    const serverMsg1 = serverStartResponse.msg1

    if (!serverMsg1?.kind || !serverMsg1.data_b64) {
      throw new Error('Invalid server msg1 format')
    }

    // Execute 4-round signing protocol
    // Round 2
    const clientR2Result = (tss as any).sign_handle(
      JSON.stringify({ bytes_b64: clientState.bytes_b64 }),
      JSON.stringify(serverMsg1)
    )
    clientState = clientR2Result[0]
    const clientMsg2 = clientR2Result[1]

    const serverR2Response = await this.apiCall(
      'POST',
      `/api/tss/${userId}/sign/round`,
      {
        sessionId,
        clientMsg: clientMsg1
      },
      projectId,
      accessToken
    )
    const serverMsg2 = this.extractServerMessage(serverR2Response)

    // Round 3
    const clientR3Result = (tss as any).sign_handle(
      JSON.stringify({ bytes_b64: clientState.bytes_b64 }),
      JSON.stringify(serverMsg2)
    )
    clientState = clientR3Result[0]
    const clientMsg3 = clientR3Result[1]

    const serverR3Response = await this.apiCall(
      'POST',
      `/api/tss/${userId}/sign/round`,
      {
        sessionId,
        clientMsg: clientMsg2
      },
      projectId,
      accessToken
    )
    const serverMsg3 = this.extractServerMessage(serverR3Response)

    // Round 4 (Final)
    const clientFinalResult = (tss as any).sign_handle(
      JSON.stringify({ bytes_b64: clientState.bytes_b64 }),
      JSON.stringify(serverMsg3)
    )

    if (!Array.isArray(clientFinalResult) || clientFinalResult.length < 3) {
      throw new Error('Invalid final sign_handle result')
    }

    const clientSignature = clientFinalResult[2]

    // Send final message to server
    await this.apiCall(
      'POST',
      `/api/tss/${userId}/sign/round`,
      {
        sessionId,
        clientMsg: clientMsg3
      },
      projectId,
      accessToken
    )

    // Format signature
    const signature = this.formatSignature(clientSignature)

    return signature
  }

  /**
   * Show transaction confirmation dialog
   */
  private async showConfirmationDialog(
    userId: string,
    projectId: string,
    project: ProjectInfo,
    origin: string,
    transaction: Transaction,
    risk: RiskAssessment,
    userProfile?: { avatar?: string | null; displayName?: string | null }
  ): Promise<{ confirmed: boolean; trustApp: boolean; appName?: string; appLogo?: string }> {
    // Request parent to show iframe
    this.showIframe()

    // Fetch project metadata
    const metadata = await this.fetchProjectMetadata(projectId)

    return new Promise((resolve) => {
      const modal = this.createConfirmationModal(
        userId,
        projectId,
        project,
        origin,
        transaction,
        risk,
        metadata,
        userProfile
      )

      const confirmBtn = modal.querySelector('.confirm-btn') as HTMLButtonElement
      const cancelBtn = modal.querySelector('.cancel-btn') as HTMLButtonElement
      const trustCheckbox = modal.querySelector('.trust-app-checkbox') as HTMLInputElement

      confirmBtn?.addEventListener('click', (e) => {
        if (e.isTrusted) {
          const trustApp = trustCheckbox?.checked || false
          modal.remove()
          this.hideIframe()
          resolve({ confirmed: true, trustApp, appName: metadata?.name, appLogo: metadata?.logo })
        }
      })

      cancelBtn?.addEventListener('click', () => {
        modal.remove()
        this.hideIframe()
        resolve({ confirmed: false, trustApp: false })
      })

      document.body.appendChild(modal)
    })
  }

  /**
   * Create confirmation modal UI
   */
  private createConfirmationModal(
    userId: string,
    projectId: string,
    project: ProjectInfo,
    origin: string,
    transaction: Transaction,
    risk: RiskAssessment,
    metadata: ProjectCard | null,
    userProfile?: { avatar?: string | null; displayName?: string | null }
  ): HTMLElement {
    const modal = document.createElement('div')
    modal.className = 'transaction-confirmation-modal'

    // Build template data
    const templateData = buildConfirmTxTemplateData(
      project,
      origin,
      transaction,
      risk,
      metadata,
      userProfile,
      this.explorerUrl
    )

    // Load and render template
    const template = TemplateLoader.getTemplate('confirm-tx')
    modal.innerHTML = TemplateEngine.render(template, templateData)

    return modal
  }

  /**
   * Assess transaction risk
   *
   * TODO: Implement backend-based risk assessment with real-time price conversion
   * - Get current LUMIA/USD price from API
   * - Calculate transaction value in USD
   * - Use dynamic thresholds based on USD value (e.g., >$100 = HIGH)
   * - Consider additional factors: recipient reputation, contract verification, etc.
   */
  private async assessRisk(transaction: Transaction): Promise<RiskAssessment> {
    const reasons: string[] = []
    let score = 0

    // Convert wei to LUMIA (18 decimals)
    const valueWei = BigInt(transaction.value || '0')
    const valueLumia = Number(valueWei) / 1e18

    // Check high value (threshold: 10 LUMIA)
    // TODO: Replace with USD-based threshold after implementing backend price conversion
    if (valueLumia > 10) {
      score += 20
      // Format with appropriate precision
      const formattedValue =
        valueLumia >= 1 ? valueLumia.toFixed(4).replace(/\.?0+$/, '') : valueLumia.toFixed(8).replace(/\.?0+$/, '')
      reasons.push(`High value transaction (${formattedValue} LUMIA)`)
    }

    // Check if contract interaction
    if (transaction.data && transaction.data !== '0x' && transaction.data.length > 2) {
      score += 10
      reasons.push('Contract interaction detected')
    }

    // Determine risk level
    let level: RiskAssessment['level']
    if (score >= 50) level = 'CRITICAL'
    else if (score >= 30) level = 'HIGH'
    else if (score >= 15) level = 'MEDIUM'
    else level = 'LOW'

    return { level, score, reasons }
  }

  /**
   * Compute transaction hash (simplified, adjust for your needs)
   */
  private computeTransactionHash(transaction: Transaction): `0x${string}` {
    // TODO: Implement proper transaction hash computation
    // For now, return a placeholder
    const encoder = new TextEncoder()
    const data = encoder.encode(JSON.stringify(transaction))

    // Simple hash (in production, use proper transaction encoding)
    let hash = 0
    for (let i = 0; i < data.length; i++) {
      hash = (hash << 5) - hash + data[i]
      hash = hash & hash
    }

    const hashHex = Math.abs(hash).toString(16).padStart(64, '0')
    return `0x${hashHex}` as `0x${string}`
  }

  private formatValue(value: string): string {
    try {
      const eth = parseFloat(value)
      return eth.toFixed(6)
    } catch {
      return value
    }
  }

  private formatSignature(sig: any): `0x${string}` {
    if (typeof sig === 'string') return sig as `0x${string}`

    if (sig && typeof sig === 'object' && sig.r_b64 && sig.s_b64) {
      const rBytes = Uint8Array.from(atob(sig.r_b64), (c) => c.charCodeAt(0))
      const sBytes = Uint8Array.from(atob(sig.s_b64), (c) => c.charCodeAt(0))

      const r = Array.from(rBytes)
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('')
      const s = Array.from(sBytes)
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('')
      const v = (Number(sig.recid) + 27).toString(16).padStart(2, '0')

      return `0x${r}${s}${v}` as `0x${string}`
    }

    throw new Error('Invalid signature format')
  }

  private hexToBytes(hex: string): Uint8Array {
    const bytes = new Uint8Array(hex.length / 2)
    for (let i = 0; i < hex.length; i += 2) {
      bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16)
    }
    return bytes
  }

  private extractServerMessage(response: any): any {
    return response?.msgNext || response?.msg_next || response?.msg || null
  }

  /**
   * Sign EIP712 typed data with user confirmation
   */
  async signTypedData(
    userId: string,
    projectId: string,
    origin: string,
    typedData: EIP712TypedData,
    digest32: `0x${string}`,
    accessToken?: string,
    userProfile?: { avatar?: string | null; displayName?: string | null },
    /** owner address of the project's wallet as TSS reports it; undefined signs with the current keyshare */
    walletOwner?: string
  ): Promise<`0x${string}`> {
    console.log('[iframe][Sign] EIP712 signing request:', {
      userId,
      projectId,
      origin,
      primaryType: typedData.primaryType
    })

    await this.initialize()

    const projectInfo: ProjectInfo = {
      id: projectId,
      name: 'Application',
      logoUrl: '',
      website: origin,
      domains: [origin]
    }

    // Check if app is trusted (auto-approve without confirmation)
    const isTrusted = this.trustedApps.isTrusted(userId, projectId, origin)

    if (!isTrusted) {
      // Show user confirmation dialog for EIP712 message
      const confirmResult = await this.showEIP712ConfirmationDialog(
        userId,
        projectId,
        projectInfo,
        origin,
        typedData,
        userProfile
      )

      if (!confirmResult.confirmed) {
        throw new UserRejectedError('User rejected signature request')
      }

      // If user checked "trust this app", save it
      if (confirmResult.trustApp) {
        this.trustedApps.addTrustedApp(userId, projectId, origin, confirmResult.appName, confirmResult.appLogo)
        // Clear any opt-out flag since user explicitly chose to trust this app
        this.optedOutApps.removeOptedOut(userId, projectId, origin)
      }
    }

    // Load keyshare from IFRAME localStorage
    const keyshareData = this.storage.loadKeyshareOf(userId, walletOwner)
    if (!keyshareData) {
      throw new Error('No keyshare found. Please complete DKG first.')
    }

    // Perform MPC signing
    const signature = await this.performMPCSigning(userId, keyshareData, digest32, projectId, accessToken, {
      kind: 'typedData',
      typedData
    })

    console.log('[iframe][Sign] EIP712 signature generated')
    return signature
  }

  /**
   * Show EIP712 confirmation dialog
   */
  private async showEIP712ConfirmationDialog(
    userId: string,
    projectId: string,
    project: ProjectInfo,
    origin: string,
    typedData: EIP712TypedData,
    userProfile?: { avatar?: string | null; displayName?: string | null }
  ): Promise<{ confirmed: boolean; trustApp: boolean; appName?: string; appLogo?: string }> {
    // Request parent to show iframe
    this.showIframe()

    // Fetch project metadata for better UX
    const metadata = await this.fetchProjectMetadata(projectId)

    return new Promise((resolve) => {
      const modal = this.createEIP712ConfirmationModal(
        userId,
        projectId,
        project,
        origin,
        typedData,
        metadata,
        userProfile
      )

      const confirmBtn = modal.querySelector('.confirm-btn') as HTMLButtonElement
      const cancelBtn = modal.querySelector('.cancel-btn') as HTMLButtonElement
      const trustCheckbox = modal.querySelector('.trust-app-checkbox') as HTMLInputElement

      confirmBtn?.addEventListener('click', (e) => {
        if (e.isTrusted) {
          const trustApp = trustCheckbox?.checked || false
          modal.remove()
          this.hideIframe()
          resolve({ confirmed: true, trustApp, appName: metadata?.name, appLogo: metadata?.logo })
        }
      })

      cancelBtn?.addEventListener('click', () => {
        modal.remove()
        this.hideIframe()
        resolve({ confirmed: false, trustApp: false })
      })

      document.body.appendChild(modal)
    })
  }

  /**
   * Create EIP712 confirmation modal UI (similar to MetaMask)
   */
  private createEIP712ConfirmationModal(
    userId: string,
    projectId: string,
    project: ProjectInfo,
    origin: string,
    typedData: EIP712TypedData,
    metadata: ProjectCard | null,
    userProfile?: { avatar?: string | null; displayName?: string | null }
  ): HTMLElement {
    const modal = document.createElement('div')
    // modal.className = 'eip712-confirmation-modal'

    // Build template data
    const templateData = buildSignTxTemplateData(project, origin, typedData, metadata, userProfile)

    // Load and render template
    const template = TemplateLoader.getTemplate('sign-eip712-tx')
    modal.innerHTML = TemplateEngine.render(template, templateData)

    return modal
  }

  /**
   * Show iframe (notify parent)
   */
  private showIframe(): void {
    if (window.parent !== window) {
      window.parent.postMessage({ type: 'IFRAME_MSG_SHOW_IFRAME', timestamp: Date.now() }, '*')
    }
  }

  /**
   * Hide iframe (notify parent)
   */
  private hideIframe(): void {
    if (window.parent !== window) {
      window.parent.postMessage({ type: 'IFRAME_MSG_HIDE_IFRAME', timestamp: Date.now() }, '*')
    }
  }
}
